import { z } from 'zod';

// T-215/T-568: anagrafica dei destinatari DDT, dalla ristorazione collettiva
// ai clienti commerciali di bar, pasticcerie e panifici. La rintracciabilità
// a valle (Reg. 178/2002 art. 18 + Accordo Stato-Regioni 28/07/2005) richiede
// per ogni cliente: ragione
// sociale, indirizzo, recapiti e un referente con potere decisionale per i
// ritiri urgenti — da qui i campi contact/phone/email.
export const DESTINATION_KINDS = [
  'bar_caffetteria',
  'pasticceria_gelateria',
  'panificio_gastronomia',
  'ristorante_pizzeria',
  'hotel_bnb',
  'negozio_rivendita',
  'supermercato_gdo',
  'catering_eventi',
  'grossista_distributore',
  'scuola',
  'ospedale',
  'rsa',
  'azienda',
  'altro',
] as const;
export const DestinationKindSchema = z.enum(DESTINATION_KINDS);
export type DestinationKind = z.infer<typeof DestinationKindSchema>;

export const DESTINATION_KIND_LABELS: Record<DestinationKind, string> = {
  bar_caffetteria: 'Bar / Caffetteria',
  pasticceria_gelateria: 'Pasticceria / Gelateria',
  panificio_gastronomia: 'Panificio / Gastronomia',
  ristorante_pizzeria: 'Ristorante / Pizzeria',
  hotel_bnb: 'Hotel / B&B',
  negozio_rivendita: 'Negozio / Rivendita',
  supermercato_gdo: 'Supermercato / GDO',
  catering_eventi: 'Catering / Eventi',
  grossista_distributore: 'Grossista / Distributore',
  scuola: 'Scuola',
  ospedale: 'Ospedale',
  rsa: 'RSA / Casa di riposo',
  azienda: 'Azienda / Mensa',
  altro: 'Altro',
};

// T-421: reparto/sotto-gruppo di una destinazione (es. ospedale → SPDC, ORTOPEDIA).
// Denormalizzato in destinations.wards (JSONB). Il preposto ordina per reparto se
// la destinazione ne ha; le righe ordine referenziano il reparto per NOME.
export const DestinationWardSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(60),
  sort_order: z.number().int().default(0),
  // T-438: accesso rapido all'ordine per letto del reparto via QR + PIN (senza
  // login). `token` = stringa lunga random STABILE nell'URL pubblico del QR
  // (trackeat.eu/r/<token>); `pin` = codice breve che il reparto digita per
  // sbloccare la pagina (anti-abuso). Additivi/opzionali sul JSONB wards →
  // nessuna migration (il sync porta l'intero wards). null = non ancora generato.
  token: z.string().max(64).nullable().optional(),
  pin: z.string().max(12).nullable().optional(),
});
export type DestinationWard = z.infer<typeof DestinationWardSchema>;

export const DestinationSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  business_id: z.string().uuid().nullable(),
  name: z.string().min(1).max(200),
  kind: DestinationKindSchema.default('scuola'),
  address: z.string().nullable(),
  city: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  contact_name: z.string().nullable(),
  notes: z.string().nullable(),
  // T-421: MODELLO = sottoinsieme di label del catalogo ordini (order_catalog_rows)
  // ordinabili per questa destinazione. null/[] = catalogo completo (retro-compat).
  order_model: z.array(z.string().max(200)).nullable().optional(),
  // T-421: REPARTI opzionali della destinazione. [] = nessun reparto → ordine a
  // livello destinazione.
  wards: z.array(DestinationWardSchema).default([]),
  version: z.number().int().nonnegative().default(0),
  created_at: z.string().datetime(),
  archived_at: z.string().datetime().nullable(),
});
export type Destination = z.infer<typeof DestinationSchema>;
